import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  InspectionReportDocument,
  InspectionReportStatus,
  UpdateInspectionReportInput,
} from '@jobos/shared';

import { apiClient } from '@/api/client';
import { jobFileKeys } from '@/hooks/useJobFiles';
import { useToast } from '@/hooks/use-toast';

export interface InspectionReport {
  id: string;
  jobId: string;
  title: string;
  status: InspectionReportStatus;
  /** When the report was marked sent. Server-derived from `status`. */
  sentAt: string | null;
  document: InspectionReportDocument;
  pdfKey: string | null;
  pdfStaleAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const inspectionReportKeys = {
  all: ['inspectionReports'] as const,
  list: (jobId: string) => [...inspectionReportKeys.all, 'list', jobId] as const,
  detail: (id: string) => [...inspectionReportKeys.all, 'detail', id] as const,
};

export function useJobInspectionReports(jobId: string | undefined) {
  return useQuery({
    queryKey: inspectionReportKeys.list(jobId!),
    queryFn: async () => {
      const res = await apiClient.get<{ reports: InspectionReport[] }>(
        `/jobs/${jobId}/inspection-reports`,
      );
      return res.reports;
    },
    enabled: !!jobId,
  });
}

export function useInspectionReport(id: string | undefined) {
  return useQuery({
    queryKey: inspectionReportKeys.detail(id!),
    queryFn: async () => {
      return apiClient.get<{ report: InspectionReport; photoUrls: Record<string, string> }>(
        `/inspection-reports/${id}`,
      );
    },
    enabled: !!id,
  });
}

export function useCreateInspectionReport(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (input: { title?: string } = {}) => {
      const res = await apiClient.post<{ report: InspectionReport }>(
        `/jobs/${jobId}/inspection-reports`,
        input,
      );
      return res.report;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inspectionReportKeys.list(jobId) });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to create report.', variant: 'destructive' });
    },
  });
}

export function useUpdateInspectionReport(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateInspectionReportInput) => {
      const res = await apiClient.patch<{ report: InspectionReport }>(`/inspection-reports/${id}`, input);
      return res.report;
    },
    onSuccess: (report) => {
      // Write the saved report over the cached detail. Queries are fresh for
      // five minutes here, so without this the editor re-opens on the document
      // it was first handed — every photo added since missing — and its next
      // autosave writes that stale copy back over the server's newer one.
      // Patched rather than invalidated: the response *is* the newest document,
      // and a refetch would re-presign every photo on every keystroke's save.
      queryClient.setQueryData<{ report: InspectionReport; photoUrls: Record<string, string> }>(
        inspectionReportKeys.detail(report.id),
        (prev) => (prev ? { ...prev, report } : prev),
      );
      queryClient.invalidateQueries({ queryKey: inspectionReportKeys.list(report.jobId) });
      // The job's file list mirrors the photos its reports hold, so placing one
      // in a slot adds an entry to the Files tab. Nothing else tells that list
      // it changed, and it is cached for five minutes.
      queryClient.invalidateQueries({ queryKey: jobFileKeys.lists(report.jobId) });
    },
  });
}

/**
 * Mark a report sent, or put it back to draft.
 *
 * Scoped to the job rather than to one report id, because the caller is the
 * job's report list and a per-id hook cannot be called in a loop. `sentAt` is
 * not sent — the server derives it from the status.
 */
export function useSetInspectionReportSent(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ id, sent }: { id: string; sent: boolean }) => {
      const res = await apiClient.patch<{ report: InspectionReport }>(
        `/inspection-reports/${id}`,
        { status: sent ? 'sent' : 'draft' },
      );
      return res.report;
    },
    onSuccess: (report) => {
      queryClient.setQueryData<{ report: InspectionReport; photoUrls: Record<string, string> }>(
        inspectionReportKeys.detail(report.id),
        (prev) => (prev ? { ...prev, report } : prev),
      );
      queryClient.invalidateQueries({ queryKey: inspectionReportKeys.list(jobId) });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update the report.',
        variant: 'destructive',
      });
    },
  });
}

export function useDeleteInspectionReport(jobId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/inspection-reports/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inspectionReportKeys.list(jobId) });
      toast({ title: 'Report deleted' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to delete report.', variant: 'destructive' });
    },
  });
}

// The editor's photo upload lives in useJobFiles (useUploadJobPhoto): a photo
// shot for a report is filed in the job's library and the slot points at that
// row. POST /inspection-reports/:id/photos is still served for reports built
// before that, but nothing in the app calls it any more.

export function useGenerateInspectionPdf(id: string) {
  const { toast } = useToast();
  return useMutation({
    mutationFn: async () => {
      const res = await apiClient.post<{ url: string }>(`/inspection-reports/${id}/pdf`);
      return res.url;
    },
    onError: (error: Error) => {
      toast({ title: 'PDF generation failed', description: error.message, variant: 'destructive' });
    },
  });
}
